// GIS Implicit Flow

interface TokenResponse {
  access_token: string
  token_type: string
  expires_in: number
  scope: string
  error?: string
  error_description?: string
  error_uri?: string
}

interface TokenClient {
  requestAccessToken: (overridableClientConfig?: { scope?: string }) => void
}

interface GoogleAccounts {
  oauth2: {
    initTokenClient: (config: {
      client_id: string
      scope: string
      callback: (response: TokenResponse) => void
      error_callback?: (error: { type: string; message?: string }) => void
    }) => TokenClient
  }
}

declare global {
  interface Window {
    google?: {
      accounts: GoogleAccounts
    }
  }
}

export function useGoogleAuth() {
  const config = useRuntimeConfig()
  const { addToast } = useToastNotifications()

  const accessToken = ref<string | null>(null)
  const isAuthenticating = ref(false)

  function loadGisScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.google?.accounts) {
        resolve()
        return
      }

      const existing = document.querySelector(
        'script[src="https://accounts.google.com/gsi/client"]',
      )
      if (existing) {
        existing.addEventListener('load', () => resolve())
        return
      }

      const script = document.createElement('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.async = true
      script.defer = true
      script.onload = () => resolve()
      script.onerror = () =>
        reject(new Error('Failed to load Google Identity Services'))
      document.head.appendChild(script)
    })
  }

  async function authenticate(): Promise<string | null> {
    isAuthenticating.value = true
    accessToken.value = null

    try {
      await loadGisScript()
    } catch {
      addToast({
        type: 'error',
        heading: 'Google Drive',
        message: 'Failed to load Google authentication. Please try again.',
      })
      isAuthenticating.value = false
      return null
    }

    return new Promise((resolve) => {
      const clientId = config.public.google?.clientId as string

      if (!clientId) {
        addToast({
          type: 'error',
          heading: 'Google Drive',
          message: 'Google Drive integration is not configured.',
        })
        isAuthenticating.value = false
        resolve(null)
        return
      }

      const tokenClient = window.google!.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'https://www.googleapis.com/auth/drive.readonly',
        callback: (response: TokenResponse) => {
          isAuthenticating.value = false

          if (response.error) {
            addToast({
              type: 'error',
              heading: 'Google Drive',
              message: 'Authentication failed. Please try again.',
            })
            resolve(null)
            return
          }

          accessToken.value = response.access_token
          resolve(response.access_token)
        },
        error_callback: (error: { type: string }) => {
          isAuthenticating.value = false

          if (error.type === 'popup_closed') {
            addToast({
              type: 'default',
              heading: 'Google Drive',
              message: 'Authentication was cancelled.',
              life: 5000,
            })
          } else {
            addToast({
              type: 'error',
              heading: 'Google Drive',
              message: 'Authentication failed. Please try again.',
            })
          }

          resolve(null)
        },
      })

      tokenClient.requestAccessToken()
    })
  }

  return {
    accessToken: readonly(accessToken),
    isAuthenticating: readonly(isAuthenticating),
    authenticate,
  }
}
